import React, { useEffect, useState } from 'react'
import {
  Box, Flex, Text, Button, Tooltip,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody,
  useDisclosure,
} from '@chakra-ui/react'
import {
  DndContext, DragOverlay, PointerSensor, useSensor, useSensors, closestCenter,
} from '@dnd-kit/core'
import { Monitor, Smartphone, Code2, Trash2, LayoutTemplate, Undo2, Redo2 } from 'lucide-react'
import BlocksSidebar from './BlocksSidebar'
import EmailCanvas from './EmailCanvas'
import PropertiesPanel from './PropertiesPanel'
import useEmailDesignerStore from './emailDesignerStore'
import { blocksToHtml } from './htmlExport'
import { blocksToAmpEmail } from './ampEmailExport'
import { BLOCK_LABELS } from './blockDefaults'
import { PRIMARY_BG } from '../../../../theme/brand';

const ACCENT = PRIMARY_BG

function DragPreview({ data }) {
  if (!data) return null
  const label = data.source === 'sidebar'
    ? (BLOCK_LABELS[data.blockType] || data.blockType)
    : 'Block'
  return (
    <Box
      bg={ACCENT}
      shadow={`0 8px 24px rgba(1,172,251,0.35)`}
      borderRadius="8px"
      px={3} py={2}
      pointerEvents="none"
      display="flex" alignItems="center" gap={2}
    >
      <Box w={2} h={2} borderRadius="full" bg="white" opacity={0.8} />
      <Text fontSize="13px" fontWeight="700" color="white">+ {label}</Text>
    </Box>
  )
}

function MobilePreview({ html }) {
  return (
    <Box
      h="100%" overflowY="auto" bg="gray.100"
      display="flex" flexDir="column" alignItems="center" py={10}
    >
      <Box
        w="375px" borderRadius="40px" overflow="hidden"
        shadow="0 16px 48px rgba(0,0,0,0.2)"
        border="8px solid" borderColor="gray.300"
        bg="white" flexShrink={0}
      >
        <Box h="20px" bg="gray.200" display="flex" alignItems="center" justifyContent="center">
          <Box w="48px" h="5px" bg="gray.400" borderRadius="full" />
        </Box>
        <iframe
          srcDoc={html || ''}
          title="Mobile Preview"
          style={{ display: 'block', width: '100%', height: '600px', border: 'none' }}
        />
      </Box>
      <Text fontSize="11px" color="gray.400" mt={4}>Mobile preview · 375px</Text>
    </Box>
  )
}

function HtmlModal({ isOpen, onClose, html }) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="5xl">
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent maxH="88vh" display="flex" flexDir="column">
        <ModalHeader fontSize="14px" fontWeight="700">Generated HTML Email</ModalHeader>
        <ModalCloseButton />
        <ModalBody overflowY="auto" pb={6}>
          <Box
            as="pre"
            fontSize="11.5px"
            fontFamily="'Fira Code', Consolas, monospace"
            bg="gray.900"
            color="green.300"
            p={5}
            borderRadius="10px"
            overflowX="auto"
            whiteSpace="pre"
            sx={{ lineHeight: 1.7 }}
          >
            {html}
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

function EmailDesigner({ onChange }) {
  const { blocks, emailSettings, addBlock, reorderBlocks, addSubBlock, reorderSubBlocks, clearAll, undo, redo } = useEmailDesignerStore()
  // Subscribe to the history stacks so the buttons enable/disable reactively.
  const canUndo = useEmailDesignerStore(s => (s._past || []).length > 0)
  const canRedo = useEmailDesignerStore(s => (s._future || []).length > 0)
  const [activeDragData, setActiveDragData] = useState(null)
  const [previewMode, setPreviewMode] = useState('desktop')
  const { isOpen: htmlOpen, onOpen: openHtml, onClose: closeHtml } = useDisclosure()

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  )

  useEffect(() => {
    if (onChange) {
      // 2nd arg = amp4email version (text/x-amp-html). Backwards-compatible:
      // callers that only read the HTML ignore it. Persist/send it to make
      // Story blocks play as AMP carousels in supporting inboxes.
      onChange(blocksToHtml(blocks, emailSettings), blocksToAmpEmail(blocks, emailSettings))
    }
  }, [blocks, emailSettings]) // eslint-disable-line react-hooks/exhaustive-deps

  // Undo/redo keyboard shortcuts (⌘/Ctrl+Z, ⇧⌘/Ctrl+Z or ⌘/Ctrl+Y).
  // Ignored while typing in an input/textarea/contentEditable so native text
  // undo keeps working inside fields.
  useEffect(() => {
    const onKey = (e) => {
      if (!(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== 'z' && e.key.toLowerCase() !== 'y') return
      const t = e.target
      const tag = (t?.tagName || '').toLowerCase()
      if (tag === 'input' || tag === 'textarea' || t?.isContentEditable) return
      e.preventDefault()
      const isRedo = e.key.toLowerCase() === 'y' || (e.key.toLowerCase() === 'z' && e.shiftKey)
      isRedo ? redo() : undo()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [undo, redo])

  function handleDragStart({ active }) {
    setActiveDragData(active.data.current || null)
  }

  function handleDragEnd({ active, over }) {
    setActiveDragData(null)
    if (!over) return
    const activeData = active.data.current
    const overData = over.data?.current
    if (!activeData) return

    // Drop from sidebar into a column's empty drop zone
    if (activeData.source === 'sidebar' && overData?.type === 'coldef') {
      addSubBlock(overData.colDefId, overData.parentBlockId, activeData.blockType, -1, activeData.propsOverride || {})
      return
    }

    // Drop from sidebar ONTO an existing element inside a column → add to that
    // same column (after the hovered element). Without this, a 2nd element
    // dropped over the 1st fell through to the top-level canvas add.
    if (activeData.source === 'sidebar' && overData?.source === 'column-subblock') {
      const parent = blocks.find(b => b.id === overData.parentBlockId)
      const col = parent?.props?.columnDefs?.find(c => c.id === overData.colDefId)
      const idx = col?.blocks?.findIndex(sb => sb.id === overData.subBlockId)
      addSubBlock(overData.colDefId, overData.parentBlockId, activeData.blockType, idx ?? -1, activeData.propsOverride || {})
      return
    }

    // Drop from sidebar onto canvas or a canvas block
    if (activeData.source === 'sidebar') {
      const overIndex = blocks.findIndex(b => b.id === over.id)
      const insertAfter = overIndex >= 0 ? overIndex : blocks.length - 1
      addBlock(activeData.blockType, insertAfter, activeData.propsOverride || {})
      return
    }

    // Reorder sub-blocks within a column
    if (activeData.source === 'column-subblock') {
      if (active.id !== over.id) {
        reorderSubBlocks(activeData.colDefId, activeData.parentBlockId, active.id, over.id)
      }
      return
    }

    // Reorder top-level canvas blocks
    if (activeData.source === 'canvas') {
      if (active.id !== over.id) {
        reorderBlocks(active.id, over.id)
      }
    }
  }

  const currentHtml = blocksToHtml(blocks, emailSettings)

  return (
    <Box h="100%" display="flex" flexDir="column" bg="white">
      {/* Inner toolbar */}
      <Flex
        align="center" px={4} h="46px" gap={3}
        bg="white"
        borderBottom="2px solid" borderColor="gray.100"
        flexShrink={0}
      >
        {/* Preview mode toggle */}
        <Flex
          bg="gray.100" borderRadius="9px" p="3px" gap="2px"
        >
          {[
            { mode: 'desktop', Icon: Monitor, label: 'Desktop view' },
            { mode: 'mobile', Icon: Smartphone, label: 'Mobile view' },
          ].map(({ mode, Icon, label }) => (
            <Tooltip key={mode} label={label} hasArrow placement="bottom" openDelay={400}>
              <Box
                px={3} py="5px" borderRadius="6px" cursor="pointer"
                bg={previewMode === mode ? 'white' : 'transparent'}
                color={previewMode === mode ? ACCENT : 'gray.400'}
                shadow={previewMode === mode ? '0 1px 4px rgba(0,0,0,0.1)' : 'none'}
                style={previewMode === mode ? { color: ACCENT } : {}}
                _hover={{ color: previewMode === mode ? ACCENT : 'gray.600' }}
                onClick={() => setPreviewMode(mode)}
                transition="all 0.13s"
                display="flex" alignItems="center" gap="6px"
              >
                <Icon size={13} />
                <Text fontSize="11px" fontWeight="600" display={previewMode === mode ? 'block' : 'none'}>
                  {mode === 'desktop' ? 'Desktop' : 'Mobile'}
                </Text>
              </Box>
            </Tooltip>
          ))}
        </Flex>

        <Box w="1px" h="18px" bg="gray.200" />

        {/* Undo / Redo */}
        <Flex gap="2px">
          {[
            { label: 'Undo (⌘Z)', Icon: Undo2, action: undo, enabled: canUndo },
            { label: 'Redo (⇧⌘Z)', Icon: Redo2, action: redo, enabled: canRedo },
          ].map(({ label, Icon, action, enabled }) => (
            <Tooltip key={label} label={label} hasArrow placement="bottom" openDelay={400}>
              <Box
                as="button" px={2} py="6px" borderRadius="6px"
                color={enabled ? 'gray.500' : 'gray.300'}
                cursor={enabled ? 'pointer' : 'not-allowed'}
                _hover={enabled ? { bg: 'gray.100', color: 'gray.700' } : {}}
                onClick={() => enabled && action()}
                disabled={!enabled}
                display="flex" alignItems="center" transition="all 0.13s"
              >
                <Icon size={14} />
              </Box>
            </Tooltip>
          ))}
        </Flex>

        <Box w="1px" h="18px" bg="gray.200" />

        {/* Block count pill */}
        <Box
          px={2.5} py={1} borderRadius="6px"
          bg={blocks.length > 0 ? `${ACCENT}12` : 'gray.50'}
          border="1px solid"
          style={{ borderColor: blocks.length > 0 ? `${ACCENT}30` : '#e2e8f0' }}
        >
          <Flex align="center" gap={1.5}>
            <LayoutTemplate size={11} color={blocks.length > 0 ? ACCENT : '#9ca3af'} />
            <Text
              fontSize="11px" fontWeight="700"
              style={{ color: blocks.length > 0 ? ACCENT : '#9ca3af' }}
            >
              {blocks.length} block{blocks.length !== 1 ? 's' : ''}
            </Text>
          </Flex>
        </Box>

        <Box flex={1} />

        {/* Right actions */}
        <Flex gap={1.5}>
          <Button
            size="sm" variant="ghost"
            leftIcon={<Code2 size={12} />}
            fontSize="11.5px" h="30px" px={3}
            color="gray.400"
            fontWeight="600"
            _hover={{ bg: 'gray.50', color: 'gray.700' }}
            onClick={openHtml}
          >
            HTML
          </Button>
          {blocks.length > 0 && (
            <>
              <Box w="1px" h="18px" bg="gray.150" alignSelf="center" />
              <Button
                size="sm" variant="ghost"
                leftIcon={<Trash2 size={12} />}
                fontSize="11.5px" h="30px" px={3}
                color="gray.400"
                fontWeight="600"
                _hover={{ bg: 'red.50', color: 'red.500' }}
                onClick={clearAll}
              >
                Clear all
              </Button>
            </>
          )}
        </Flex>
      </Flex>

      {/* 3-panel layout */}
      <Flex flex={1} overflow="hidden">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <BlocksSidebar />

          <Box flex={1} overflow="hidden">
            {previewMode === 'mobile'
              ? <MobilePreview html={currentHtml} />
              : <EmailCanvas />
            }
          </Box>

          <PropertiesPanel />

          <DragOverlay dropAnimation={null}>
            <DragPreview data={activeDragData} />
          </DragOverlay>
        </DndContext>
      </Flex>

      <HtmlModal isOpen={htmlOpen} onClose={closeHtml} html={currentHtml} />
    </Box>
  )
}

export default EmailDesigner
